import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import type { ICompaniesRepository } from "App/Modules/Repositories";
import type { ICompany } from "App/Modules/Models";
import { PatchDelta } from "App/Modules/Services";

import "./_edit-company-dialog.scss";
import { ConfirmDeletion } from "../ConfirmDeletion";

type EditCompanyDialogProps = {
    company: ICompany;
    onClose: () => void;
    onDeleteSuccess: () => void;
    mmCompaniesRepository: ICompaniesRepository;
    onEditSuccess?: (company: ICompany) => void;
};

export const EditCompanyDialog = (props: EditCompanyDialogProps) => {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const [corporateIdNumber, setCorporateIdNumber] = useState("");
    const [companies, setCompanies] = useState<ICompany[]>([]);

    const [selectedMergeCompany, setSelectedMergeCompany] = useState<ICompany>(null);
    const [mergeCompanyName, setMergeCompanyName] = useState("");

    const [saveError, setSaveError] = useState(false);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        if (props.company) {
            setName(props.company.name);
            setCorporateIdNumber(props.company.corporateIdNumber || "");

            props.mmCompaniesRepository.getAllCompanies(props.company?.partner?.id).subscribe(
                (response) => {
                    setCompanies(response.data.filter((c) => c.id !== props.company.id));
                },
                () => {
                    setLoadError(true);
                },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.company]);

    const deleteCompany = () => {
        setSaveError(false);
        props.mmCompaniesRepository.deleteCompany(props.company.id).subscribe(
            () => {
                props.onClose();
                props.onDeleteSuccess();
            },
            () => {
                setSaveError(true);
            },
        );
    };

    const saveCompany = () => {
        setSaveError(false);
        if (selectedMergeCompany) {
            props.mmCompaniesRepository.mergeCompany(props.company.id, selectedMergeCompany.id).subscribe(() => {
                props.onDeleteSuccess();
                props.onClose();
            });
        } else {
            const editedCompany = { ...props.company, name, corporateIdNumber };
            const patchObject = PatchDelta.difference(props.company, editedCompany);
            props.mmCompaniesRepository.patchCompany(props.company.id, patchObject).subscribe(
                (response) => {
                    if (props.onEditSuccess) {
                        props.onEditSuccess(response.data);
                        props.onClose();
                    }
                },
                () => {
                    setSaveError(true);
                },
            );
        }
    };

    return (
        <div className="card dialog edit-company-dialog">
            <div className="dialog__section dialog__section--full">
                <h1 className="title title--h1 title--no-spacing">
                    {`${t("Entities.Common.EditEntity")} ${t("BeforeLogin.Company.Company")}`}
                </h1>
                <p className="paragraph">
                    {t("Entities.Common.EditOrMerge", {
                        entityName: t("BeforeLogin.Company.Company"),
                    })}
                </p>
            </div>

            <div className="dialog__section dialog__section--full">
                <label className="caption" htmlFor="name">
                    {t("Entities.Common.Name")}
                </label>
                <TextField
                    id="name"
                    required
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="text"
                    autoComplete="off"
                    value={name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setName(e.target.value);
                    }}
                />
            </div>

            <div className="dialog__section dialog__section--full">
                <label className="caption" htmlFor="corporate-id-number">
                    {t("Entities.Common.CorporateIdNumber")}
                </label>
                <TextField
                    id="corporate-id-number"
                    required
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="text"
                    autoComplete="off"
                    value={corporateIdNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setCorporateIdNumber(e.target.value);
                    }}
                />
            </div>

            <div className="dialog__section dialog__section--full">
                <label className="caption" htmlFor="manufacturer">
                    {t("Gui.Manage.Label.DeleteMerge")}
                </label>
                <Autocomplete
                    id="merge-company"
                    options={companies}
                    getOptionLabel={(p) => p.name}
                    value={selectedMergeCompany}
                    inputValue={mergeCompanyName}
                    onChange={(e, value: ICompany) => setSelectedMergeCompany(value)}
                    onInputChange={(e, value) => setMergeCompanyName(value)}
                    renderInput={(params) => (
                        <TextField {...params} variant="outlined" size="small" data-testid="select-merge-company" />
                    )}
                />
            </div>

            {loadError ? (
                <div className="dialog__section dialog__section--full dialog__section--noscroll">
                    <div className="notice notice--critical">
                        <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                        <p className="caption">{t("Error.ErrorLoading")}</p>
                    </div>
                </div>
            ) : null}

            {saveError ? (
                <div className="dialog__section dialog__section--full dialog__section--noscroll">
                    <div className="notice notice--critical">
                        <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                        <p className="caption">{t("Error.ErrorSaving")}</p>
                    </div>
                </div>
            ) : null}

            <div className="dialog__section dialog__section--buttons">
                <ConfirmDeletion
                    onUserConfirm={deleteCompany}
                    titleText={t("Entities.Confirm.Deletion")}
                    bodyText={t("Entities.Confirm.Deletion.AreYouSure")}
                />

                <Button color="default" onClick={() => props.onClose()}>
                    {t("Gui.Buttons.Cancel")}
                </Button>

                <Button
                    color="primary"
                    variant="contained"
                    onClick={() => {
                        saveCompany();
                    }}
                >
                    {t("Gui.Buttons.Save")}
                </Button>
            </div>
        </div>
    );
};
